import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './HoverTip.css';

const GAP_PX = 12;
const EDGE_PX = 8;

/** In-house hover/focus tooltip. Use instead of the native `title` attribute. */
export const HoverTip: React.FC<{
  text?: string | null;
  id?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ text, id, className, children }) => {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const prefersBelow = /\bis-below\b/.test(className ?? '');
  const extraClass = (className ?? '')
    .replace(/\bis-below\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const updatePlace = useCallback(() => {
    const wrap = wrapRef.current;
    const tip = tipRef.current;
    if (!wrap || !tip) return;
    const wrapRect = wrap.getBoundingClientRect();
    const tipHeight = tip.offsetHeight;
    const tipWidth = tip.offsetWidth;
    const spaceAbove = wrapRect.top - GAP_PX;
    const spaceBelow = window.innerHeight - wrapRect.bottom - GAP_PX;
    let below = prefersBelow;
    if (spaceAbove >= tipHeight && spaceBelow >= tipHeight) below = prefersBelow;
    else if (spaceAbove >= tipHeight) below = false;
    else if (spaceBelow >= tipHeight) below = true;
    else below = spaceBelow > spaceAbove;
    const top = below ? wrapRect.bottom + GAP_PX : wrapRect.top - GAP_PX - tipHeight;
    const half = tipWidth / 2;
    const centerX = wrapRect.left + wrapRect.width / 2;
    const left = Math.min(window.innerWidth - EDGE_PX - half, Math.max(EDGE_PX + half, centerX));
    setCoords({ top, left });
  }, [prefersBelow]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updatePlace();
    const onMove = () => updatePlace();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, updatePlace]);

  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  if (!text) return <>{children}</>;
  const tip = (
    <span
      ref={tipRef}
      className={`tb-hover-tip${open && coords ? ' is-open' : ''}`}
      id={id}
      role="tooltip"
      style={coords ? { top: coords.top, left: coords.left } : undefined}
    >
      {text}
    </span>
  );
  return (
    <span
      ref={wrapRef}
      className={`tb-hover-tip-wrap${extraClass ? ` ${extraClass}` : ''}`}
      aria-describedby={id}
      onPointerEnter={show}
      onPointerLeave={hide}
      onFocusCapture={show}
      onBlurCapture={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        hide();
      }}
    >
      {children}
      {open ? createPortal(tip, document.body) : null}
    </span>
  );
};
